import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Form, FormProvider, useFieldArray, useForm} from "react-hook-form";
import * as XLSX from "xlsx";
import styled, {css} from "styled-components";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowRight,
  faArrowsLeftRight,
  faArrowsUpDownLeftRight,
  faClose,
  faEye,
  faEyeSlash
} from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";

// Contexts
import {FacilityNavContext} from "../../contexts/facilitynav";

// Hooks
import useApi from "../../hooks/useApi";
import usePrevious from "../../hooks/usePrevious.js";

// Utils
import {CHECKSHEET, EVENT} from "../../utils/report.js";
import {getAncestors, getFields, mergeBuilders} from "../../utils/builder.js";
import {exists, getAlpha, getNumeric} from "../../utils/helpers.js";

// Components
import Modal from "../../components/Modal.js";
import SearchSelect from "../../components/SearchSelect.js";
import {
  InputSelect,
  InputRadioGroup,
  InputError,
  InputCheck
} from "../../components/form/FormInputs.js";

// Style
import {z} from "../../style/components/mixins.js";
import {border, pad, radius} from "../../style/components/variables";
import {
  Button,
  Label,
  FormField,
  Text,
  Inline,
  Abbr,
  ButtonWrapper,
  scrollbar,
  Error,
  HeadingCenter,
  FormFieldWrapper,
  Warning,
  Small
} from "../../style/components/general";

const ROW = "Row";
const COLUMN = "Column";

const statsInitial = [
  {name: "hasMin", enabled: false},
  {name: "hasAvg", enabled: false},
  {name: "hasMedian", enabled: false},
  {name: "hasMax", enabled: false},
  {name: "hasTotal", enabled: false}
];

const statLabels = {
  hasMin: "Min",
  hasAvg: "Avg",
  hasMedian: "Median",
  hasMax: "Max",
  hasTotal: "Total"
};

const aggregationTypes = {
  total: "Total",
  avg: "Avg",
  median: "Median",
  min: "Min",
  max: "Max"
};

const ModalSectionExcel = ({
  visible,
  setVisible,
  targetWorkbook,
  targetSheet,
  targetDirection,
  setTargetDirection,
  handleAddSection,
  start,
  end,
  base,
  interval,
  targetSection,
  sections
}) => {
  const {
    facility: {types, builder: facilityBuilder}
  } = useContext(FacilityNavContext);

  const sectionDefault = targetSection
    ? {
        targetFacility: targetSection.targetFacility,
        targetSource: `${targetSection.sourceId}`,
        targetType: targetSection.sourceType,
        fields: Object.fromEntries(
          targetSection.mappings.map(mapping => [
            `${mapping.startColumn - (targetDirection === ROW ? 0 : 1)}_${
              mapping.startRow - (targetDirection === ROW ? 1 : 0)
            }`,
            {name: mapping.key, stat: mapping.stat}
          ])
        ),
        tableType: targetSection.cellInterval,
        stage: targetSection.stage
      }
    : {};

  const DEFAULT = {
    ...sectionDefault,
    targetDirection: targetDirection || ROW
  };

  const {api: apiReport} = useApi("reports");
  const {api: apiChecksheet} = useApi("checksheets");
  const {api: apiEvent} = useApi("events");

  const [rows, setRows] = useState();
  const [merges, setMerges] = useState([]);
  const [headerA, setHeaderA] = useState(targetSection?.headers?.[0]);
  const [headerB, setHeaderB] = useState(targetSection?.headers?.[1]);
  const [checksheets, setChecksheets] = useState([]);
  const [events, setEvents] = useState([]);
  const [results, setResults] = useState([]);

  const schema = yup.object().shape({
    targetFacility: yup.string().required("Please provide a target facility type."),
    targetType: yup.string().required("Please provide a target source type."),
    targetSource: yup.string().required("Please provide a target source."),
    fields: yup.object().test({
      message: "Please map all fields.",
      test: fields => {
        const fieldKeys = fields ? Object.keys(fields) : [];
        return (
          fields &&
          fieldKeys?.filter(key => typeof fields[key] === "string" && fields[key].length > 0) !==
            fieldKeys
        );
      }
    }),
    hasStats: yup.bool(),
    stats: yup
      .mixed()
      .nullable()
      .when("hasStats", {
        is: val => !!val,
        then: () =>
          yup
            .array()
            .of(yup.object())
            .test({
              test: val =>
                Array.isArray(val) && val.length > 0 && val.some(({enabled}) => !!enabled),
              message: "Please select at least one stat"
            })
      })
  });

  const form = useForm({defaultValues: DEFAULT, resolver: yupResolver(schema)});
  const {
    watch,
    setValue,
    handleSubmit,
    control,
    formState: {errors, submitCount}
  } = form;

  const {
    fields: stats,
    remove: removeStat,
    insert: insertStat
  } = useFieldArray({control, name: "stats"});

  const watchTargetDirection = watch("targetDirection");
  const watchTargetFacility = watch("targetFacility");
  const watchTargetType = watch("targetType");
  const watchTargetSource = watch("targetSource");
  const watchTargetStage = watch("targetStage");
  const watchHasStats = watch("hasStats");
  const watchTableType = watch("tableType");
  const watchFields = watch("fields");

  useEffect(() => {
    if (targetWorkbook && targetSheet) {
      const tableData = XLSX.utils.sheet_to_json(targetWorkbook.Sheets[targetSheet], {
        defval: null,
        header: 1
      });

      if (tableData?.length > 0) {
        const headers = tableData[0].map(header =>
          typeof header === "string" && header.startsWith("EMPTY__") ? null : header
        );
        const matrix = [headers]; // Start with headers
        const dataRows = tableData.slice(1); // Skip the first row (headers)
        matrix.push(...dataRows);

        setMerges(targetWorkbook.Sheets[targetSheet]["!merges"] || []);
        setRows(tableData);
      } else setRows([]);
    }
  }, [targetSheet, targetWorkbook]);

  const getMergedCellSpan = (rowIndex, colIndex) => {
    let offset = {rowSpan: 1, colSpan: 1};
    merges?.map(({s, e}) => {
      if (rowIndex >= s.r && rowIndex <= e.r && colIndex >= s.c && colIndex <= e.c) {
        const rowSpan = e.r - s.r + 1;
        const colSpan = e.c - s.c + 1;
        offset = {
          rowSpan: rowIndex === s.r ? rowSpan : 0,
          colSpan: colIndex === s.c ? colSpan : 0
        };
      }
    });
    return offset;
  };

  const getCodeIndeces = code => {
    // Split Code based on transition from alpha to numeric
    const [col, row] = code.split(/(?<=[a-zA-Z])(?=\d)/);
    return [getNumeric(col), Number(row) - 1]; // conver letter offset row
  };

  const [colA, rowA] = useMemo(() => (headerA ? getCodeIndeces(headerA) : []), [headerA]);
  const [colB, rowB] = useMemo(() => (headerB ? getCodeIndeces(headerB) : []), [headerB]);

  // Normalize the range (account for any ordering of points)
  const headerRange = useMemo(
    () => ({
      colMin: exists(colA) && exists(colB) ? Math.min(colA, colB) : undefined,
      colMax: exists(colA) && exists(colB) ? Math.max(colA, colB) : undefined,
      rowMin: exists(rowA) && exists(rowB) ? Math.min(rowA, rowB) : undefined,
      rowMax: exists(rowA) && exists(rowB) ? Math.max(rowA, rowB) : undefined
    }),
    [rowA, rowB, colA, colB]
  );

  // Get target header values from selected range
  const targetHeaders = useMemo(() => {
    if (!rows) return [];

    const {rowMin, rowMax, colMin, colMax} = headerRange;

    // Subset of target row based on target min and max
    if (watchTargetDirection === ROW && exists(rowMax) && exists(colMin) && exists(colMax))
      return rows[Number(rowMax)]?.slice(colMin, colMax + 1);

    // Subset of target column based on target min and max
    if (watchTargetDirection === COLUMN && exists(colMax) && exists(rowMin) && exists(rowMax))
      return rows?.map(row => row[Number(colMax)])?.slice(rowMin, rowMax + 1);

    return [];
  }, [watchTargetDirection, headerRange, rows]);

  const getChecksheets = useCallback(
    () =>
      apiChecksheet.callGet(null, {facilityId: watchTargetFacility, withBuilder: true}).then(({status, data}) => {
        if (status === 200 && data) setChecksheets(data);
      }),
    [apiChecksheet, watchTargetFacility]
  );

  const getEvents = useCallback(
    () =>
      apiEvent.callGet(null, {facilityId: watchTargetFacility}).then(({status, data}) => {
        if (status === 200 && data) setEvents(data);
      }),
    [apiEvent, watchTargetFacility]
  );

  useEffect(() => {
    if (watchTargetFacility) {
      getChecksheets();
      getEvents();
    }
  }, [watchTargetFacility, getChecksheets, getEvents]);

  const sourceOptions = useMemo(() => {
    if (watchTargetType === CHECKSHEET && checksheets?.length > 0)
      return checksheets.map(({id, name, frequency, ...rest}) => ({
        name: id,
        label: frequency?.name ? `${frequency.name.toUpperCase()} ${name}` : name,
        ...rest
      }));

    // only allow events with existing records
    if (watchTargetType === EVENT && events?.length > 0)
      return events
        .filter(({lastCompleted}) => lastCompleted)
        .map(({id, name}) => ({
          name: id,
          label: name
        }));

    return [];
  }, [watchTargetType, checksheets, events]);

  // Get source based on selected sourceType
  const selectedChecksheet = useMemo(() => {
    if (watchTargetType === CHECKSHEET && checksheets?.length > 0 && watchTargetSource)
      return checksheets.find(({id}) => `${id}` === watchTargetSource);
    return undefined;
  }, [watchTargetType, watchTargetSource, checksheets]);

  const selectedEvent = useMemo(() => {
    if (watchTargetType === EVENT && events?.length > 0 && watchTargetSource)
      return events.find(({id}) => `${id}` === watchTargetSource);
    return undefined;
  }, [watchTargetType, watchTargetSource, events]);

  // Get target stage based on selected event
  const stageOptions = useMemo(() => {
    if (watchTargetType === EVENT && selectedEvent)
      return Object.keys(selectedEvent.stages.byId).map(k => ({
        name: k,
        label: selectedEvent.stages.byId[k].name?.toUpperCase?.()
      }));
    return undefined;
  }, [watchTargetType, selectedEvent]);

  const selectedStage = useMemo(() => {
    const {byId} = selectedEvent?.stages || {};
    if (byId && watchTargetStage in byId) return byId[watchTargetStage];
    return undefined;
  }, [selectedEvent, watchTargetStage]);

  // Get available fields based on selected source
  const fieldOptions = useMemo(() => {
    let builder;

    if (watchTargetType === CHECKSHEET && watchTargetSource && selectedChecksheet) {
      builder = mergeBuilders(selectedChecksheet?.builder, facilityBuilder);
    }

    if (watchTargetType === EVENT && watchTargetSource && selectedStage)
      builder = selectedStage?.builder;

    if (builder)
      return getFields(builder.allIds, builder.byId, null, [], true)
        .filter(field => field.type === "number" || field.type === "generated")
        .map(({name, label, ancestry, units}) => ({name, label: ancestry || label, units}));

    return undefined;
  }, [watchTargetType, watchTargetSource, selectedChecksheet, selectedStage, facilityBuilder]);

  const search = query => {
    if (!fieldOptions) return;
    const dateOption =
      watchFields && Object.values(watchFields).some(selected => selected?.name === "date")
        ? []
        : [{name: "date", label: "Date"}];
    if (query && fieldOptions) {
      const lower = query.toLowerCase();
      const tempResults = fieldOptions.filter(option => {
        if (option.label.toLowerCase().includes(lower)) {
          const filtered = watchFields
            ? Object.values(watchFields).filter(selected => selected?.name === option.name)
            : [];
          if (
            (watchTableType !== "day" && filtered.length < 5) ||
            (watchTableType === "day" && filtered.length === 0)
          )
            return true;
        }
        return false;
      });
      tempResults.sort(
        (a, b) => a.label.toLowerCase().indexOf(lower) - b.label.toLowerCase().indexOf(lower)
      );
      setResults([...dateOption, ...tempResults]);
    } else
      setResults([
        ...dateOption,
        ...fieldOptions.filter(option => {
          const filtered = watchFields
            ? Object.values(watchFields).filter(selected => selected?.name === option.name)
            : [];
          if (
            (watchTableType !== "day" && filtered.length < 5) ||
            (watchTableType === "day" && filtered.length === 0)
          )
            return true;
          return false;
        })
      ]);
  };

  const overlapError = useMemo(() => {
    const overlapping = sections?.filter(({headers}) => {
      const [existingA, existingB] = headers;

      if (existingA && existingB && exists(colA) && exists(colB) && exists(rowA) && exists(rowB)) {
        const [eColA, eRowA] = getCodeIndeces(existingA);
        const [eColB, eRowB] = getCodeIndeces(existingB);
        return !(eColB < colA || eColA > colB || eRowB < rowA || eRowA > rowB);
      }
      return false;
    });
    return overlapping?.length > 0;
  }, [sections, colA, colB, rowA, rowB]);

  const warnOverlap = useMemo(() => {
    const overlapping = sections?.filter(({headers}) => {
      const [existingA, existingB] = headers;
      if (existingA && existingB && headerRange) {
        const [eColA, eRowA] = getCodeIndeces(existingA);
        const [eColB, eRowB] = getCodeIndeces(existingB);

        if (watchTargetDirection === "Row")
          return eColA >= headerRange.colMin || eColB <= headerRange.colMax;
        if (watchTargetDirection === "Column")
          return eRowA >= headerRange.rowMin || eRowB <= headerRange.rowMax;
      }
      return false;
    });

    return overlapping?.length > 0;
  }, [sections, headerRange, watchTargetDirection]);

  const createSection = useCallback(
    ({
      targetType,
      targetFacility,
      targetSource,
      targetDirection: dir,
      targetStage,
      fields,
      hasStats,
      stats: statsEnabled,
      tableType: cellInterval
    }) => {
      const {rowMin, rowMax, colMin, colMax} = headerRange;
      const headers = [`${getAlpha(colMin)}${rowMin + 1}`, `${getAlpha(colMax)}${rowMax + 1}`];

      // Check for overlaps before creating
      const mappings = Object.keys(fields)?.map(key => {
        const [col, row] = key.split("_");
        const startColumn = dir === COLUMN ? Number(col) + 1 : Number(col);
        const startRow = dir === ROW ? Number(row) + 1 : Number(row);

        return {
          key: fields[key].name,
          startColumn,
          startRow,
          stat: cellInterval !== "day" ? fields[key].stat : null
        };
      });

      const params = {
        excelIn: true,
        targetDirection: dir,
        sections: [
          {
            targetSheet,
            sourceType: targetType,
            sourceId: targetSource,
            stage: targetStage,
            headers,
            mappings,
            stats: hasStats ? statsEnabled : null,
            cellInterval,
            targetFacility
          }
        ],
        start,
        end,
        base,
        interval
      };

      apiReport.callPost(params).then(({status, data}) => {
        if (status === 201 && data) {
          if (!targetDirection) setTargetDirection(dir);
          handleAddSection(data[0]);
        }
      });
    },
    [
      apiReport,
      targetSheet,
      headerRange,
      base,
      end,
      interval,
      start,
      handleAddSection,
      targetDirection,
      setTargetDirection
    ]
  );

  const onDragEnd = result => {
    const {destination, source: dragSource} = result;
    if (
      !destination ||
      dragSource?.index === destination.index ||
      destination.droppableId !== dragSource.droppableId
    ) {
      return;
    }
    const movingField = watch(`${dragSource.droppableId}.${dragSource.index}`);
    let sourceOffset = 0;
    let destinationOffset = 1;
    if (destination.index < dragSource.index) {
      sourceOffset = 1;
      destinationOffset = 0;
    }

    if (dragSource.droppableId === "stats") {
      insertStat(destination.index + destinationOffset, {...movingField});
      removeStat(dragSource.index + sourceOffset);
    }
  };

  useEffect(() => {
    if (watchHasStats) setValue("stats", statsInitial);
  }, [watchHasStats, setValue]);

  const getAvailStatTypes = useCallback(
    (fieldName, key) => {
      const typeNames = Object.keys(aggregationTypes);
      if (!watchFields || !Object.keys(watchFields).length)
        return typeNames.map(typeName => ({name: typeName, label: aggregationTypes[typeName]}));
      const available = [];
      for (let i = 0; i < typeNames.length; i++) {
        const typeName = typeNames[i];
        if (
          !Object.keys(watchFields).some(
            selectedKey =>
              watchFields[selectedKey]?.name === fieldName &&
              watchFields[selectedKey]?.stat === typeName &&
              selectedKey !== key
          )
        )
          available.push({name: typeName, label: aggregationTypes[typeName]});
      }
      return available;
    },
    [watchFields]
  );

  const prevTableType = usePrevious(watchTableType);

  useEffect(() => {
    if (prevTableType !== watchTableType && watchFields) {
      if (watchTableType === "day") {
        Object.keys(watchFields).map(key => {
          setValue(`fields.${key}.stat`, null);
        });
      } else {
        Object.keys(watchFields).map(key => {
          setValue(`fields.${key}.stat`, "total");
        });
      }
    }
  }, [watchFields, watchTableType, prevTableType, setValue]);

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <FormProvider {...form}>
        <Form noValidate onSubmit={handleSubmit(createSection)}>
          <HeadingCenter>Add Section</HeadingCenter>

          {rows?.length === 0 && <Error>Target sheet is empty, please select another.</Error>}

          {rows?.length > 0 && (
            <FormField>
              <Label bold>SELECT TARGET ROW TO MAP TO DATA...</Label>
              <ScrollWrapper>
                <ExcelTable>
                  <thead>
                    <Row>
                      <HeaderCell colSpan={1} />
                      {rows?.length > 0 &&
                        rows[0]?.map((_cell, colIndex) => (
                          <HeaderCell key={getAlpha(colIndex)}>
                            <Text bold>{getAlpha(colIndex)}</Text>
                          </HeaderCell>
                        ))}
                    </Row>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIdx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Row key={rowIdx}>
                        <Cell>
                          <Text bold>{rowIdx + 1}</Text>
                        </Cell>
                        {row.map((cell, colIdx) => {
                          const {rowSpan, colSpan} = getMergedCellSpan(rowIdx, colIdx);
                          // Skip merged cells that are spanned by others
                          if (rowSpan === 0 || colSpan === 0) return null;
                          return (
                            // eslint-disable-next-line react/no-array-index-key
                            <Cell key={colIdx} rowSpan={rowSpan} colSpan={colSpan}>
                              <CellLabel title={cell}>{cell || ""}</CellLabel>
                              <CellButtonRow>
                                {Array.from({length: colSpan}, (_, i) => i).map((_, spanColIdx) => (
                                  // eslint-disable-next-line react/no-array-index-key
                                  <CellButtonColumn key={spanColIdx}>
                                    {Array.from({length: rowSpan}, (_empty, i) => i).map(
                                      (_empty, spanRowIdx) => {
                                        const {rowMin, rowMax, colMin, colMax} = headerRange;

                                        let isActive = false;

                                        const targetRow = rowIdx + spanRowIdx;
                                        const targetCol = colIdx + spanColIdx;

                                        if (
                                          exists(colA) &&
                                          exists(rowA) &&
                                          exists(colB) &&
                                          exists(rowB)
                                        ) {
                                          // Check if the target cell is within the normalized range
                                          isActive =
                                            targetRow >= rowMin &&
                                            targetRow <= rowMax &&
                                            targetCol >= colMin &&
                                            targetCol <= colMax;
                                        } else if (exists(colA) && exists(rowA)) {
                                          // Single point match for A
                                          isActive =
                                            targetRow === Number(rowA) &&
                                            targetCol === Number(colA);
                                        } else if (exists(colB) && exists(rowB)) {
                                          // Single point match for B
                                          isActive =
                                            targetRow === Number(rowB) &&
                                            targetCol === Number(colB);
                                        }

                                        return (
                                          <CellButton
                                            key={`${targetCol}${targetRow}`}
                                            type="button"
                                            onClick={() => {
                                              const code = `${getAlpha(targetCol)}${targetRow + 1}`;
                                              if (!headerA) setHeaderA(code);
                                              else if (headerA === code) setHeaderA(null);
                                              else if (headerA && !headerB) setHeaderB(code);
                                              else if (headerB === code) setHeaderB(null);
                                              else setHeaderB(code);
                                            }}
                                            active={isActive}
                                            width={100 / colSpan}
                                            height={100 / rowSpan}
                                          />
                                        );
                                      }
                                    )}
                                  </CellButtonColumn>
                                ))}
                              </CellButtonRow>
                            </Cell>
                          );
                        })}
                      </Row>
                    ))}
                  </tbody>
                </ExcelTable>
              </ScrollWrapper>
            </FormField>
          )}

          {!targetSection && overlapError && (
            <Error>
              This section would overlap an existing section, please select new headers.
            </Error>
          )}

          {!overlapError && targetHeaders?.length > 0 && types?.length > 0 && (
            <>
              <FormField>
                <InputRadioGroup
                  name="tableType"
                  label="Select Table Type"
                  options={[
                    {label: "Standard", value: "day"},
                    {label: "By Month", value: "month"},
                    {label: "By Year", value: "year"}
                  ]}
                  defaultValue="day"
                />
              </FormField>
              <FormField>
                <InputSelect
                  name="targetFacility"
                  label="Select source facility"
                  placeholder="Source Facility..."
                  options={types.map(({facilityId, name}) => ({
                    name: facilityId,
                    label: name
                  }))}
                />
              </FormField>
            </>
          )}

          {watchTargetFacility && (
            <FormField>
              <InputSelect
                name="targetType"
                label="Select target source type"
                placeholder="Source type..."
                options={["Checksheet", "Event"]}
              />
            </FormField>
          )}

          {sourceOptions?.length > 0 && (
            <FormField>
              <InputSelect
                name="targetSource"
                label="Select target data source"
                placeholder="Source..."
                options={sourceOptions}
              />
            </FormField>
          )}

          {watchTargetType === EVENT && stageOptions && (
            <FormField>
              <InputSelect
                name="targetStage"
                placeholder="Stage..."
                label="Select target stage"
                options={stageOptions}
              />
            </FormField>
          )}

          {watchTargetSource &&
            (selectedChecksheet || (selectedEvent && selectedStage)) &&
            targetHeaders && (
              <>
                <FormField>
                  <InputRadioGroup
                    name="targetDirection"
                    label={`Build Sheet based on ${watchTargetDirection}`}
                    options={[ROW, COLUMN]}
                    defaultValue={ROW}
                    disabled={!!targetDirection}
                  />
                </FormField>
                <FormField direction={watchTargetDirection}>
                  <MappingTable>
                    <thead>
                      <tr>
                        <th>Heading</th>
                        <th>
                          <FontAwesomeIcon icon={faArrowsLeftRight} />
                        </th>
                        <th>Field</th>
                        <th>Direction</th>
                      </tr>
                    </thead>
                    <tbody>
                      {targetHeaders?.map((cell, cellIdx) => {
                        const selectedTask = selectedChecksheet || selectedStage;

                        const targetCol =
                          watchTargetDirection === ROW
                            ? cellIdx + 1 + headerRange.colMin
                            : headerRange.colMax + 1;
                        const targetRow =
                          watchTargetDirection === ROW
                            ? headerRange.rowMax + 1
                            : cellIdx + 1 + headerRange.rowMin;

                        const locKey = `${targetCol}_${targetRow}`;
                        const mappedFieldKey = `fields.${locKey}`;
                        const {name: fieldKey} = watch(mappedFieldKey) || {};
                        let key = fieldKey;
                        if (fieldKey?.match(/_comment$/)) {
                          const [updated] = fieldKey.split("_comment");
                          key = updated;
                        }

                        const targetField = selectedTask.builder.byId[key];
                        let fullName = targetField?.label
                          ? `${getAncestors(targetField.parentName, selectedTask.builder.byId)} ${
                              targetField.label
                            }`
                          : "DATE";

                        if (fieldKey?.match(/_comment$/)) fullName += " COMMENT";

                        const target = merges.find(
                          ({s, e}) =>
                            targetRow >= s.r + 1 &&
                            targetRow <= e.r + 1 &&
                            targetCol >= s.c + 1 &&
                            targetCol <= e.c + 1
                        );

                        const cellHeader = target ? rows?.[target.s.r]?.[target.s.c] : "_";

                        return (
                          <Mapping key={mappedFieldKey} direction={watchTargetDirection}>
                            <MappingCell>
                              <Text bold>
                                <Abbr title={cell || cellHeader}>{cell || cellHeader}</Abbr>
                                &nbsp;(
                                {getAlpha(targetCol - 1)}
                                {targetRow})
                              </Text>
                            </MappingCell>
                            <MappingCell>
                              <FontAwesomeIcon icon={faArrowsLeftRight} />
                            </MappingCell>
                            <MappingCell>
                              {fieldOptions.length === 0 ? (
                                <Text>No Fields Available</Text>
                              ) : (
                                <div>
                                  {fieldOptions?.length > 0 && !watch(mappedFieldKey) ? (
                                    <SearchSelect
                                      results={results}
                                      setResults={setResults}
                                      search={search}
                                      add={field => setValue(`${mappedFieldKey}.name`, field.name)}
                                      showAll
                                      large
                                      keepOpen
                                    />
                                  ) : (
                                    <Inline>
                                      <Text bold>
                                        <Abbr title={watch(mappedFieldKey)}>{fullName}</Abbr>
                                      </Text>
                                      {watchTableType !== "day" && fieldKey !== "date" && (
                                        <SelectWrapper>
                                          <InputSelect
                                            name={`${mappedFieldKey}.stat`}
                                            options={getAvailStatTypes(fieldKey, locKey)}
                                          />
                                        </SelectWrapper>
                                      )}
                                      <ButtonWrapper
                                        type="button"
                                        onClick={() => setValue(mappedFieldKey, null)}>
                                        <FontAwesomeIcon icon={faClose} />
                                      </ButtonWrapper>
                                    </Inline>
                                  )}
                                </div>
                              )}
                            </MappingCell>
                            <MappingCell>
                              <FontAwesomeIcon
                                icon={watchTargetDirection === "Row" ? faArrowDown : faArrowRight}
                              />
                            </MappingCell>
                          </Mapping>
                        );
                      })}
                    </tbody>
                  </MappingTable>
                  <InputError errors={errors} name="fields" />
                </FormField>
                <FormField>
                  <>
                    <InputCheck name="hasStats" showError={false}>
                      Include Stats?
                    </InputCheck>
                    {watchHasStats && (
                      <StatsWrapper>
                        <DragDropContext onDragEnd={onDragEnd}>
                          <Droppable droppableId="stats">
                            {outerProvided => (
                              <div {...outerProvided.droppableProps} ref={outerProvided.innerRef}>
                                {stats.map((_v, i) => {
                                  const enabled = watch(`stats.${i}.enabled`);
                                  const name = watch(`stats.${i}.name`);

                                  return (
                                    <Draggable draggableId={name} index={i} key={name}>
                                      {innerProvided => (
                                        <>
                                          <ColumnInfo
                                            enabled={enabled}
                                            ref={innerProvided.innerRef}
                                            {...innerProvided.draggableProps}
                                            {...innerProvided.dragHandleProps}>
                                            <Inline key={name}>
                                              <FontAwesomeIcon icon={faArrowsUpDownLeftRight} />
                                              {statLabels[name]?.toUpperCase()}
                                            </Inline>
                                            <Enable
                                              type="button"
                                              title="Toggle Visibility"
                                              onClick={() => {
                                                setValue(`stats.${i}.enabled`, !enabled, {
                                                  shouldDirty: true,
                                                  shouldValidate: !!submitCount
                                                });
                                              }}>
                                              <FontAwesomeIcon
                                                icon={enabled ? faEye : faEyeSlash}
                                              />
                                            </Enable>
                                          </ColumnInfo>
                                          {innerProvided.placeholder}
                                        </>
                                      )}
                                    </Draggable>
                                  );
                                })}
                                {outerProvided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                      </StatsWrapper>
                    )}
                  </>
                  <InputError errors={errors} name="stats" />
                </FormField>

                {!targetSection && warnOverlap && (
                  <WarningWrapper>
                    <Warning>Warning!</Warning>
                    <Small secondary>
                      Data aggregated from another section may overlap this section. Update or
                      proceed with caution!
                    </Small>
                  </WarningWrapper>
                )}
                <Button type="submit">{targetSection ? "Edit" : "Add"} Section</Button>
              </>
            )}
        </Form>
      </FormProvider>
    </Modal>
  );
};

ModalSectionExcel.propTypes = {
  visible: PropTypes.bool,
  setVisible: PropTypes.func.isRequired,
  targetWorkbook: PropTypes.objectOf(PropTypes.any).isRequired,
  targetSheet: PropTypes.string.isRequired,
  targetDirection: PropTypes.string,
  setTargetDirection: PropTypes.func.isRequired,
  handleAddSection: PropTypes.func.isRequired,
  start: PropTypes.string,
  end: PropTypes.string,
  base: PropTypes.string,
  interval: PropTypes.string,
  targetSection: PropTypes.objectOf(PropTypes.any),
  sections: PropTypes.arrayOf(PropTypes.any)
};

// Style Overrides
const ScrollWrapper = styled.div`
  width: 100%;
  max-height: 300px;
  margin: ${pad}px 0;
  overflow: scroll;
  ${scrollbar};
`;

const ExcelTable = styled.table`
  table-layout: fixed;
`;

const HeaderCell = styled.th`
  border: ${border} solid ${({theme}) => theme.secondary};

  ${Text} {
    text-align: center;
  }
`;

const Row = styled.tr`
  width: 100%;
`;

const Cell = styled.td`
  position: relative;
  text-align: center;
  border: ${border} solid ${({theme}) => theme.secondary};
  width: 150px;
  min-width: 150px;
  overflow: hidden;

  ${Text} {
    text-align: center;
  }
`;

const CellLabel = styled(Abbr)`
  position: absolute;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  height: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  pointer-events: none;
  z-index: ${z("above")};
`;

const CellButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const CellButtonColumn = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const CellButton = styled.button`
  width: 100%;
  height: 100%;

  ${({active}) =>
    active &&
    css`
      background: ${({theme}) => theme.primary};
    `};
`;

const MappingTable = styled.table`
  border-collapse: separate;
  border-spacing: 0 ${pad}px;

  th {
    text-align: left;
    padding: ${pad}px;
  }
`;

const MappingCell = styled.td`
  padding: ${pad}px;
  border-top: ${border} solid ${({theme}) => theme.secondary};
  border-bottom: ${border} solid ${({theme}) => theme.secondary};
`;

const Mapping = styled.tr`
  margin-bottom: ${pad / 2}px;
  padding: ${pad}px;

  ${MappingCell}:first-child {
    border-left: ${border} solid ${({theme}) => theme.secondary};
    border-top-left-radius: ${radius};
    border-bottom-left-radius: ${radius};
  }

  ${MappingCell}:last-child {
    border-right: ${border} solid ${({theme}) => theme.secondary};
    border-top-right-radius: ${radius};
    border-bottom-right-radius: ${radius};
  }
`;

const StatsWrapper = styled.div`
  width: 150px;
`;

const SelectWrapper = styled.div`
  width: 80px;
  flex-shrink: 0;
`;

const ColumnInfo = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${pad}px;
  color: ${props => props.theme.tertiary};
  background: ${props => props.theme.secondary};
  border-radius: ${radius};
  margin-top: ${pad}px;

  ${({enabled}) =>
    enabled === false &&
    css`
      opacity: 0.6;
    `}

  ${FormFieldWrapper} {
    margin: 0;
  }

  ${Inline}:first-child {
    ${({hasAggregateOptions}) =>
      hasAggregateOptions
        ? css`
            width: calc(90% - 150px);
          `
        : css`
            width: 90%;
          `}
  }

  ${Inline}:last-child {
    ${({hasAggregateOptions}) =>
      hasAggregateOptions
        ? css`
            width: calc(10% + 150px);
          `
        : css`
            width: 10%;
          `}
    justify-content: end;
    gap: ${pad}px;
  }

  svg {
    fill: ${props => props.theme.tertiary};
  }

  ${Abbr} {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  input {
    background: ${props => props.theme.tertiary};
    max-width: 150px;
  }

  ${Button} {
    padding: 0 ${pad}px;
    background: transparent;
  }
`;

const Enable = styled(Button)`
  margin-right: 0px;
`;

const WarningWrapper = styled.div`
  padding: ${pad / 2}px;
  margin-bottom: ${pad}px;
  border-radius: ${radius};
  border: ${border} solid ${({theme}) => theme.warning};
`;

export default ModalSectionExcel;
